import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../utils/currency';
import {
  ShoppingCartIcon,
  UserIcon,
  PlusIcon,
  TrashIcon,
  PrinterIcon,
  DocumentArrowDownIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import jsPDF from 'jspdf';

export default function SalePage() {
  const { user } = useAuth();
  const { formatCurrency } = useCurrency(user?.gym_id);

  const [members, setMembers] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [gymProfile, setGymProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successSale, setSuccessSale] = useState(null);

  // Form State
  const [isGuest, setIsGuest] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [memberSearch, setMemberSearch] = useState('');

  const [saleItems, setSaleItems] = useState([
    { item_id: '', quantity: 1, unit_price: 0, discount_amount: 0, tax_amount: 0, available_stock: 0, item_name: '' }
  ]);

  const [overallDiscount, setOverallDiscount] = useState(0);
  const [overallTax, setOverallTax] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [amountPaid, setAmountPaid] = useState('');
  const [notes, setNotes] = useState('');
  const [overrideStock, setOverrideStock] = useState(false);

  const receiptRef = useRef();

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [membersRes, itemsRes, gymRes] = await Promise.all([
        api.get('/api/members?per_page=500').catch(() => ({ data: { members: [] } })),
        api.get('/api/inventory/items').catch(() => ({ data: { items: [] } })),
        api.get('/api/gym/profile').catch(() => ({ data: { gym: {} } }))
      ]);

      setMembers(membersRes.data.members || []);
      setInventoryItems(itemsRes.data.items || []);
      setGymProfile(gymRes.data.gym || { name: 'SmartGoNext Gym' });
    } catch (err) {
      console.error('Failed to load POS sale data:', err);
      setError('Failed to load inventory and members for POS.');
    } finally {
      setLoading(false);
    }
  };

  // Item row change
  const handleItemRowChange = (index, field, value) => {
    const updated = [...saleItems];
    const target = updated[index];

    if (field === 'item_id') {
      const selected = inventoryItems.find(i => String(i.id) === String(value));
      if (selected) {
        target.item_id = selected.id;
        target.item_name = selected.item_name;
        target.unit_price = selected.selling_price || 0;
        target.available_stock = selected.quantity || 0;
      } else {
        target.item_id = '';
        target.item_name = '';
        target.unit_price = 0;
        target.available_stock = 0;
      }
    } else if (field === 'quantity') {
      target.quantity = Math.max(1, parseInt(value) || 1);
    } else if (field === 'unit_price') {
      target.unit_price = Math.max(0, parseFloat(value) || 0);
    } else if (field === 'discount_amount') {
      target.discount_amount = Math.max(0, parseFloat(value) || 0);
    } else if (field === 'tax_amount') {
      target.tax_amount = Math.max(0, parseFloat(value) || 0);
    }

    setSaleItems(updated);
  };

  const addSaleItemRow = () => {
    setSaleItems(prev => [
      ...prev,
      { item_id: '', quantity: 1, unit_price: 0, discount_amount: 0, tax_amount: 0, available_stock: 0, item_name: '' }
    ]);
  };

  const removeSaleItemRow = (index) => {
    if (saleItems.length === 1) return;
    setSaleItems(prev => prev.filter((_, i) => i !== index));
  };

  // Totals calculations
  const subtotal = saleItems.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
  const itemsDiscount = saleItems.reduce((sum, item) => sum + item.discount_amount, 0);
  const itemsTax = saleItems.reduce((sum, item) => sum + item.tax_amount, 0);

  const totalDiscount = itemsDiscount + (parseFloat(overallDiscount) || 0);
  const totalTax = itemsTax + (parseFloat(overallTax) || 0);

  const grandTotal = Math.max(subtotal - totalDiscount + totalTax, 0);
  const effectivePaid = amountPaid === '' ? grandTotal : Math.min(parseFloat(amountPaid) || 0, grandTotal);
  const balanceDue = grandTotal - effectivePaid;

  const filteredMembers = members.filter(m =>
    `${m.first_name} ${m.last_name}`.toLowerCase().includes(memberSearch.toLowerCase()) ||
    m.phone.includes(memberSearch) ||
    m.member_id.toLowerCase().includes(memberSearch.toLowerCase())
  );

  const handleSubmitSale = async (e) => {
    e.preventDefault();
    setError('');

    if (!isGuest && !selectedMemberId) {
      setError('Please select a member or switch to Guest / Walk-in Customer.');
      return;
    }
    if (isGuest && !customerName.trim()) {
      setError('Please enter guest customer name.');
      return;
    }

    const invalidItem = saleItems.find(i => !i.item_id);
    if (invalidItem) {
      setError('Please select an inventory item for all product rows.');
      return;
    }

    // Stock check
    for (const row of saleItems) {
      if (row.quantity > row.available_stock && !overrideStock) {
        setError(`Requested quantity (${row.quantity}) exceeds available stock (${row.available_stock}) for "${row.item_name}". Check stock override if authorized.`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = {
        member_id: !isGuest ? parseInt(selectedMemberId) : null,
        customer_name: isGuest ? customerName.trim() : null,
        is_guest: isGuest,
        items: saleItems.map(i => ({
          item_id: i.item_id,
          quantity: i.quantity,
          unit_price: i.unit_price,
          discount_amount: i.discount_amount,
          tax_amount: i.tax_amount
        })),
        overall_discount: parseFloat(overallDiscount) || 0,
        overall_tax: parseFloat(overallTax) || 0,
        payment_method: paymentMethod,
        amount_paid: effectivePaid,
        notes: notes.trim(),
        override_stock: overrideStock
      };

      const res = await api.post('/api/sales', payload);
      setSuccessSale(res.data.sale);
      fetchInitialData(); // Refresh stock
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to complete sale transaction');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSuccessSale(null);
    setSelectedMemberId('');
    setCustomerName('');
    setIsGuest(false);
    setSaleItems([{ item_id: '', quantity: 1, unit_price: 0, discount_amount: 0, tax_amount: 0, available_stock: 0, item_name: '' }]);
    setOverallDiscount(0);
    setOverallTax(0);
    setAmountPaid('');
    setNotes('');
    setError('');
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    if (!successSale) return;
    const doc = new jsPDF({
      unit: 'mm',
      format: [80, 220]
    });

    const gymName = gymProfile?.name || 'SmartGoNext Gym';
    doc.setFontSize(11);
    doc.text(gymName, 40, 10, { align: 'center' });
    doc.setFontSize(7);
    if (gymProfile?.address) doc.text(gymProfile.address, 40, 14, { align: 'center' });
    if (gymProfile?.phone) doc.text(`Ph: ${gymProfile.phone}`, 40, 18, { align: 'center' });
    doc.setFontSize(8);
    doc.text('PRODUCT SALE INVOICE', 40, 24, { align: 'center' });
    doc.text('------------------------------------------', 5, 27);

    doc.setFontSize(7);
    doc.text(`Invoice #: ${successSale.invoice_number}`, 5, 32);
    doc.text(`Date: ${new Date(successSale.created_at).toLocaleString()}`, 5, 36);
    doc.text(`Customer: ${successSale.customer_name}`, 5, 40);
    doc.text(`Payment Method: ${successSale.payment_method}`, 5, 44);
    doc.text('------------------------------------------', 5, 48);

    let y = 53;
    doc.setFontSize(7);
    successSale.items.forEach(item => {
      doc.text(`${item.item_name}`, 5, y);
      doc.text(`${item.quantity} x ${formatCurrency(item.unit_price)}`, 45, y);
      doc.text(`${formatCurrency(item.line_total)}`, 75, y, { align: 'right' });
      y += 5;
    });

    doc.text('------------------------------------------', 5, y);
    y += 4;
    doc.text(`Subtotal: ${formatCurrency(successSale.subtotal)}`, 75, y, { align: 'right' });
    if (successSale.discount_amount > 0) {
      y += 4;
      doc.text(`Discount: -${formatCurrency(successSale.discount_amount)}`, 75, y, { align: 'right' });
    }
    if (successSale.tax_amount > 0) {
      y += 4;
      doc.text(`Tax (GST): +${formatCurrency(successSale.tax_amount)}`, 75, y, { align: 'right' });
    }
    y += 5;
    doc.setFontSize(8);
    doc.text(`Grand Total: ${formatCurrency(successSale.grand_total)}`, 75, y, { align: 'right' });
    y += 4;
    doc.setFontSize(7);
    doc.text(`Paid (${successSale.payment_method}): ${formatCurrency(successSale.amount_paid)}`, 75, y, { align: 'right' });
    if (successSale.balance_due > 0) {
      y += 4;
      doc.text(`Balance Due: ${formatCurrency(successSale.balance_due)}`, 75, y, { align: 'right' });
    }

    y += 8;
    doc.text('Thank you for your purchase!', 40, y, { align: 'center' });
    doc.save(`Receipt_${successSale.invoice_number}.pdf`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500 font-medium">Loading POS Sale module...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl mx-auto pb-12">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ShoppingCartIcon className="w-7 h-7 text-indigo-600" /> POS Product Sale
          </h1>
          <p className="text-sm text-gray-500">Sell inventory supplements, apparel, and gear directly to members.</p>
        </div>
        {successSale && (
          <button
            onClick={resetForm}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-sm transition-all"
          >
            + New Sale
          </button>
        )}
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="w-5 h-5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* POS Screen Layout */}
      {!successSale ? (
        <form onSubmit={handleSubmitSale} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Customer & Item Picker */}
          <div className="lg:col-span-2 space-y-6">
            {/* Customer Selection Card */}
            <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                  <UserIcon className="w-5 h-5 text-indigo-600" /> Customer Information
                </h3>
                <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-gray-700 bg-gray-100 px-3 py-1.5 rounded-xl hover:bg-gray-200 transition-colors">
                  <input
                    type="checkbox"
                    checked={isGuest}
                    onChange={(e) => setIsGuest(e.target.checked)}
                    className="rounded text-indigo-600 focus:ring-indigo-500"
                  />
                  <span>Walk-in / Guest Customer</span>
                </label>
              </div>

              {!isGuest ? (
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                    Select Member *
                  </label>
                  <select
                    value={selectedMemberId}
                    onChange={(e) => setSelectedMemberId(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-indigo-500"
                  >
                    <option value="">-- Choose Member --</option>
                    {members.map(m => (
                      <option key={m.id} value={m.id}>
                        {m.first_name} {m.last_name} ({m.member_id} - {m.phone})
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                    Guest Name *
                  </label>
                  <input
                    type="text"
                    placeholder="Enter guest/customer name"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-indigo-500"
                  />
                </div>
              )}
            </div>

            {/* Items Picker Card */}
            <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-bold text-gray-900">Sale Items</h3>
                <button
                  type="button"
                  onClick={addSaleItemRow}
                  className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 font-bold rounded-xl text-xs flex items-center gap-1 transition-colors"
                >
                  <PlusIcon className="w-4 h-4" /> Add Item Row
                </button>
              </div>

              <div className="space-y-3">
                {saleItems.map((row, idx) => (
                  <div key={idx} className="p-4 bg-gray-50 border border-gray-200 rounded-2xl space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-center">
                      <div className="sm:col-span-5">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Item *</label>
                        <select
                          value={row.item_id}
                          onChange={(e) => handleItemRowChange(idx, 'item_id', e.target.value)}
                          className="w-full bg-white border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-indigo-500"
                        >
                          <option value="">Select Inventory Item</option>
                          {inventoryItems.map(item => (
                            <option key={item.id} value={item.id}>
                              {item.item_name} (Stock: {item.quantity} | {formatCurrency(item.selling_price)})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="sm:col-span-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Qty</label>
                        <input
                          type="number"
                          min="1"
                          value={row.quantity}
                          onChange={(e) => handleItemRowChange(idx, 'quantity', e.target.value)}
                          className="w-full bg-white border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-indigo-500 text-center"
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Price</label>
                        <input
                          type="number"
                          step="0.01"
                          value={row.unit_price}
                          onChange={(e) => handleItemRowChange(idx, 'unit_price', e.target.value)}
                          className="w-full bg-white border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-indigo-500 text-right"
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Total</label>
                        <div className="text-sm font-bold text-gray-800 text-right py-2">
                          {formatCurrency((row.unit_price * row.quantity) - row.discount_amount + row.tax_amount)}
                        </div>
                      </div>

                      <div className="sm:col-span-1 flex justify-end">
                        {saleItems.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeSaleItemRow(idx)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Stock Alert & Stock Override Checkbox */}
                    {row.item_id && (
                      <div className="flex items-center justify-between text-xs pt-1 border-t border-gray-200/60">
                        <span className={row.quantity > row.available_stock ? 'text-red-600 font-bold' : 'text-gray-500'}>
                          Available Stock: {row.available_stock} {row.quantity > row.available_stock ? '(Exceeded!)' : ''}
                        </span>
                        {row.quantity > row.available_stock && (
                          <label className="flex items-center gap-1.5 text-red-700 font-semibold cursor-pointer">
                            <input
                              type="checkbox"
                              checked={overrideStock}
                              onChange={(e) => setOverrideStock(e.target.checked)}
                              className="rounded text-red-600"
                            />
                            <span>Override Negative Stock</span>
                          </label>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column: Bill Summary & Checkout */}
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
              <h3 className="text-base font-bold text-gray-900 border-b border-gray-100 pb-3">
                Bill Summary
              </h3>

              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span className="font-medium text-gray-900">{formatCurrency(subtotal)}</span>
                </div>

                <div className="flex justify-between items-center">
                  <span>Discount</span>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0"
                    value={overallDiscount}
                    onChange={(e) => setOverallDiscount(e.target.value)}
                    className="w-24 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1 text-right text-xs focus:outline-none focus:border-indigo-500"
                  />
                </div>

                <div className="flex justify-between items-center">
                  <span>Tax (GST)</span>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0"
                    value={overallTax}
                    onChange={(e) => setOverallTax(e.target.value)}
                    className="w-24 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1 text-right text-xs focus:outline-none focus:border-indigo-500"
                  />
                </div>

                <div className="border-t border-gray-200 pt-3 flex justify-between items-center font-bold text-base text-gray-900">
                  <span>Grand Total</span>
                  <span className="text-indigo-600 text-lg">{formatCurrency(grandTotal)}</span>
                </div>
              </div>

              {/* Payment Info */}
              <div className="space-y-3 pt-3 border-t border-gray-100">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
                    Payment Method
                  </label>
                  <select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-indigo-500 font-medium"
                  >
                    <option value="Cash">Cash</option>
                    <option value="UPI">UPI / GPay / PhonePe</option>
                    <option value="Card">Credit / Debit Card</option>
                    <option value="Razorpay">Razorpay / Online</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
                    Amount Paid
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder={grandTotal.toString()}
                    value={amountPaid}
                    onChange={(e) => setAmountPaid(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-indigo-500 font-bold text-gray-900"
                  />
                </div>

                {balanceDue > 0 && (
                  <div className="p-2.5 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl text-xs flex justify-between font-bold">
                    <span>Balance Due:</span>
                    <span>{formatCurrency(balanceDue)}</span>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
                    Sale Notes (Optional)
                  </label>
                  <textarea
                    rows="2"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Reference, batch number, etc."
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs focus:outline-none focus:border-indigo-500 resize-none"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow-md transition-all disabled:opacity-50 text-sm"
              >
                {submitting ? 'Processing Transaction...' : 'Complete & Generate Bill'}
              </button>
            </div>
          </div>
        </form>
      ) : (
        /* Printable Thermal Receipt View */
        <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-xl max-w-xl mx-auto space-y-6">
          <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-2xl flex items-center justify-between text-sm">
            <div className="flex items-center gap-2 font-bold">
              <CheckCircleIcon className="w-5 h-5 text-emerald-600" />
              <span>Sale Recorded Successfully!</span>
            </div>
            <span className="font-mono text-xs bg-emerald-100 text-emerald-800 px-2.5 py-1 rounded-lg">
              {successSale.invoice_number}
            </span>
          </div>

          {/* Printable Receipt Container */}
          <div ref={receiptRef} className="print-thermal border border-gray-300 p-6 rounded-xl font-mono text-xs space-y-4 bg-gray-50 text-gray-900 max-w-sm mx-auto shadow-sm">
            {/* Gym Header with Logo */}
            <div className="text-center space-y-1 pb-2 border-b border-gray-300">
              {gymProfile?.logo && (
                <img
                  src={gymProfile.logo.startsWith('http') || gymProfile.logo.startsWith('data:') ? gymProfile.logo : `${process.env.REACT_APP_API_URL || ''}${gymProfile.logo}`}
                  alt="Gym Logo"
                  className="w-12 h-12 object-contain mx-auto mb-1 rounded-full border"
                />
              )}
              <h2 className="text-base font-bold uppercase tracking-wider text-gray-900">{gymProfile?.name || 'SmartGoNext Gym'}</h2>
              {gymProfile?.address && <p className="text-[10px] text-gray-600">{gymProfile.address}</p>}
              {gymProfile?.phone && <p className="text-[10px] text-gray-600">Ph: {gymProfile.phone}</p>}
              <p className="text-[10px] text-indigo-600 font-bold uppercase tracking-wide pt-1">Product Sale Invoice</p>
            </div>

            {/* Customer & Bill Meta */}
            <div className="space-y-0.5 text-[11px] pb-2 border-b border-gray-300">
              <div className="flex justify-between">
                <span><strong>Invoice #:</strong></span>
                <span className="font-bold">{successSale.invoice_number}</span>
              </div>
              <div className="flex justify-between">
                <span><strong>Date & Time:</strong></span>
                <span>{new Date(successSale.created_at).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span><strong>Customer:</strong></span>
                <span className="font-bold text-gray-900">{successSale.customer_name}</span>
              </div>
              <div className="flex justify-between">
                <span><strong>Payment Method:</strong></span>
                <span className="font-bold text-indigo-700">{successSale.payment_method}</span>
              </div>
            </div>

            {/* Billed Items List */}
            <div className="space-y-2 pb-2 border-b border-gray-300">
              <div className="flex justify-between font-bold text-[10px] uppercase text-gray-500 border-b border-gray-200 pb-1">
                <span>Item</span>
                <span>Qty x Price</span>
                <span>Total</span>
              </div>
              {successSale.items.map((item, idx) => (
                <div key={idx} className="flex justify-between items-start text-[11px]">
                  <div className="max-w-[130px] font-medium text-gray-900">{item.item_name}</div>
                  <div className="text-[10px] text-gray-500">{item.quantity} × {formatCurrency(item.unit_price)}</div>
                  <div className="font-bold text-gray-900">{formatCurrency(item.line_total)}</div>
                </div>
              ))}
            </div>

            {/* Financial Summary */}
            <div className="space-y-1 text-right text-[11px]">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal:</span>
                <span>{formatCurrency(successSale.subtotal)}</span>
              </div>
              {successSale.discount_amount > 0 && (
                <div className="flex justify-between text-red-600 font-semibold">
                  <span>Discount:</span>
                  <span>-{formatCurrency(successSale.discount_amount)}</span>
                </div>
              )}
              {successSale.tax_amount > 0 && (
                <div className="flex justify-between text-gray-600">
                  <span>Tax (GST):</span>
                  <span>+{formatCurrency(successSale.tax_amount)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-xs pt-1.5 border-t border-gray-400 text-gray-900">
                <span>Grand Total:</span>
                <span className="text-indigo-600">{formatCurrency(successSale.grand_total)}</span>
              </div>
              <div className="flex justify-between text-[11px] pt-0.5">
                <span>Amount Paid ({successSale.payment_method}):</span>
                <span className="font-bold text-emerald-700">{formatCurrency(successSale.amount_paid)}</span>
              </div>
              {successSale.balance_due > 0 && (
                <div className="flex justify-between font-bold text-amber-700 pt-0.5">
                  <span>Balance Due:</span>
                  <span>{formatCurrency(successSale.balance_due)}</span>
                </div>
              )}
            </div>

            <div className="text-center pt-3 border-t border-gray-300 text-[10px] text-gray-500 space-y-0.5">
              <p className="font-semibold text-gray-700">Thank you for shopping with us!</p>
              <p>Powered by SmartGoNext Gym</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3 justify-center pt-2">
            <button
              onClick={handlePrint}
              className="px-5 py-2.5 bg-gray-900 hover:bg-black text-white font-bold rounded-xl text-sm flex items-center gap-2 shadow-sm transition-all"
            >
              <PrinterIcon className="w-5 h-5" /> Thermal Print
            </button>

            <button
              onClick={handleDownloadPDF}
              className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl text-sm flex items-center gap-2 shadow-sm transition-all"
            >
              <DocumentArrowDownIcon className="w-5 h-5" /> Download PDF
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
